/**
 * Translation helper function for bilingual support
 * 
 * @param {string} key - Translation key (supports nested keys like 'error.variant_not_selected')
 * @param {object} [params] - Optional parameters to replace in translation
 * @returns {string} Translated string
 * 
 * @example
 * t('error.variant_not_selected')
 * t('error.file_too_large', { maxSize: '20 MB' })
 * t('properties.holes')
 */
function t(key, params = {}) {
    // Get current locale from Shopify or HTML lang attribute
    let locale = 'en';
    if (typeof Shopify !== 'undefined' && Shopify.locale) {
      const langCode = Shopify.locale.split('-')[0];
      if (langCode) locale = langCode;
    } else if (document.documentElement && document.documentElement.lang) {
      const langCode = document.documentElement.lang.split('-')[0];
      if (langCode) locale = langCode;
    }
  
    // Translation dictionary - organized by category
    const translations = {
      en: {
        error: {
          variant_not_selected: 'Error: Variant not selected. Please select a variant.',
          file_too_large: 'Your file exceeds {{ maxSize }}. Cannot add to cart.',
          file_too_large_message: 'File exceeds the {{ maxSize }} limit. Please upload a smaller file.',
          add_to_cart_failed: 'Error: Failed to add to cart',
          generic: 'An error occurred',
          fetch_cart_failed: 'Failed to fetch cart (status: {{ status }})',
          cart_empty: 'Cart is empty',
          no_checkout_url: 'No checkout URL returned from server',
          no_custom_items: 'No custom items',
          draft_order_failed: 'Failed to create draft order. Please try again.',
          custom_products_load_failed: 'Unable to load product configuration. Please refresh the page and try again.',
          custom_products_not_loaded: 'Product configuration is still loading. Please wait a moment.',
        },
        ui: {
          processing: 'Processing...',
        },
        properties: {
          width: 'Width',
          height: 'Height',
          panel_height: 'Height',
          panelHeight: 'Height',
          ramp_height: 'Ramp height',
          rampHeight: 'Ramp height',
          diagonal: 'Diagonal',
          holes: 'Number of holes',
          corners: 'Number of corners',
          notches: 'Notches',
          cuts: 'Angle cuts',
          stair_steps: 'Number of steps',
          stairSteps: 'Number of steps',
          sides: 'Number of sides',
          plan: 'Plan',
        },
      },
      fr: {
        error: {
          variant_not_selected: 'Erreur: Variante non sélectionnée. Veuillez sélectionner une variante.',
          file_too_large: 'Votre fichier dépasse {{ maxSize }}. Impossible de l\'ajouter au panier.',
          file_too_large_message: 'Le fichier dépasse la limite de {{ maxSize }}. Veuillez téléverser un fichier plus léger.',
          add_to_cart_failed: 'Erreur: Impossible d\'ajouter au panier',
          generic: 'Une erreur est survenue',
          fetch_cart_failed: 'Échec de la récupération du panier (statut: {{ status }})',
          cart_empty: 'Le panier est vide',
          no_checkout_url: 'Aucune URL de paiement retournée par le serveur',
          no_custom_items: 'Aucun article personnalisé',
          draft_order_failed: 'Échec de la création de la commande. Veuillez réessayer.',
          custom_products_load_failed: 'Impossible de charger la configuration du produit. Veuillez actualiser la page et réessayer.',
          custom_products_not_loaded: 'La configuration du produit est en cours de chargement. Veuillez patienter un instant.',
        },
        ui: {
          processing: 'Traitement en cours...',
        },
        properties: {
          width: 'Largeur',
          height: 'Hauteur',
          panel_height: 'Hauteur',
          panelHeight: 'Hauteur',
          ramp_height: 'Hauteur rampe',
          rampHeight: 'Hauteur rampe',
          diagonal: 'Diagonale',
          holes: 'Nombre de trous',
          corners: 'Nombre de coins',
          notches: 'Encoches',
          cuts: 'Coupes d\'angle',
          stair_steps: 'Nombre de marches',
          stairSteps: 'Nombre de marches',
          sides: 'Nombre de côtés',
          plan: 'Plan',
        },
      },
    };
  
    // Helper function to get nested value from object
    /**
     * @param {unknown} obj
     * @param {string} path
     * @returns {unknown}
     */
    function getNestedValue(obj, path) {
      let current = obj;
      for (const segment of path.split('.')) {
        if (!current || typeof current !== 'object') return undefined;
        current = /** @type {Record<string, unknown>} */ (current)[segment];
      }
      return current;
    }
  
    // Get translation for current locale, fallback to English
    const normalizedLocale = locale === 'fr' ? 'fr' : 'en';
    const localeTranslations = translations[normalizedLocale];
    const translatedForLocale = getNestedValue(localeTranslations, key);
    const translatedFallbackEn = getNestedValue(translations.en, key);
    let translation = typeof translatedForLocale === 'string'
      ? translatedForLocale
      : (typeof translatedFallbackEn === 'string' ? translatedFallbackEn : key);
  
    // Replace parameters in translation (e.g., {{ maxSize }})
    if (params && typeof params === 'object') {
      const safeParams = /** @type {Record<string, unknown>} */ (params);
      Object.keys(safeParams).forEach(param => {
        translation = translation.replace(new RegExp(`{{\\s*${param}\\s*}}`, 'g'), String(safeParams[param]));
      });
    }
  
    return translation;
  }

/**
 * Format property label for display in cart
 * Converts camelCase to Title Case, with optional custom translation
 * 
 * @param {string} propertyName - Property name (e.g., 'holes')
 * @returns {string} Formatted label (e.g., 'Number of holes')
 * 
 * @example
 * formatPropertyLabel('holes') // Returns translated label
 */
function formatPropertyLabel(propertyName) {
  if (!propertyName) return '';
  
  // Try to get translation first
  const translationKey = `properties.${propertyName}`;
  const translated = t(translationKey);
  
  // If translation exists and is different from the key, use it
  if (translated && translated !== translationKey) {
    return translated;
  }
  
  // Fallback: Convert camelCase to Title Case
  return propertyName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
}

/**
 * Update property labels in cart items
 * Should be called after cart DOM is updated
 */
function updateCartPropertyLabels() {
  const propertyLabels = document.querySelectorAll('.cart-items__properties dt');
  
  propertyLabels.forEach(dt => {
    const originalText = dt.textContent.trim();
    // Remove trailing colon if present
    const propertyName = originalText.replace(/:\s*$/, '');
    
    // Format the label
    const formattedLabel = formatPropertyLabel(propertyName);
    
    // Update if different
    if (formattedLabel && formattedLabel !== propertyName) {
      dt.textContent = formattedLabel + ':';
    }
  });
}
  
  // Make functions available globally
  if (typeof window !== 'undefined') {
    window.t = t;
    window.formatPropertyLabel = formatPropertyLabel;
    window.updateCartPropertyLabels = updateCartPropertyLabels;
  }
  